/**
 * AnomalyResolver - Enhanced Anomaly Resolution Interface
 * =========================================================
 * 
 * Advanced anomaly resolution component with:
 * - Revenue timeline with anomaly markers and hover details
 * - Diagnostic AI suggestions (not generic restatements)
 * - Category breakdown showing deltas: "Catering: $28K → $42K (+$14.4K)"
 * - Top transactions driving each anomaly
 * - Batch resolution with filters (spikes, drops, pre-loss, loss-period)
 * - Resolutions flow directly into expert report narrative
 * 
 * Works for ALL business types - categories come from actual data.
 */

import React, { useState, useMemo } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  X,
  Check,
  Sparkles,
  Loader2,
  TrendingUp,
  TrendingDown,
  FileText,
  Zap,
  Filter,
  RefreshCw,
  ArrowRight,
  List
} from 'lucide-react';

// Resolution reasons that work for any business type
const RESOLUTION_REASONS = [
  "Seasonal variation",
  "Loss event impact", 
  "Insurance proceeds",
  "One-time event",
  "Misclassification",
  "Recovery period",
  "COVID-19 impact",
  "Normal fluctuation",
  "Verified with client",
  "Requires further review"
];

// Batch filter options
const BATCH_FILTERS = [
  { id: 'all', label: 'All Unresolved', color: 'gray' },
  { id: 'spikes', label: 'Spikes Only', color: 'amber' },
  { id: 'drops', label: 'Drops Only', color: 'red' },
  { id: 'high', label: 'High Priority', color: 'orange' },
  { id: 'pre-loss', label: 'Pre-Loss Period', color: 'blue' },
  { id: 'loss-period', label: 'Loss Period', color: 'purple' },
];

interface CategoryBreakdown {
  name: string;
  prev: number;
  curr: number;
  delta: number;
}

interface FlaggedTransaction {
  id?: string;
  date: string;
  description: string;
  amount: number;
  flag: boolean;
  category?: string;
}

interface EnhancedAnomaly {
  id: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  type: string;
  title: string;
  description: string;
  percent: number;
  from: number;
  to: number;
  periodFrom: string;
  periodTo: string;
  period: string;
  deviation: number;
  categories?: CategoryBreakdown[];
  transactions?: FlaggedTransaction[];
  aiSuggestion?: string;
  aiConfidence?: number;
  sourceDoc?: string;
  lossEvent?: boolean;
  is_resolved?: boolean;
  resolution_note?: string;
  resolved_at?: string;
  resolution_reason?: string;
  // Event period flags
  isPreLoss?: boolean;
  isLossPeriod?: boolean;
  isPostLoss?: boolean;
  // Original anomaly fields
  anomaly_type?: string;
  explanation?: string;
  ai_analysis?: {
    explanation?: string;
    likely_false_positive?: boolean;
    recommendation?: string;
    confidence?: number;
  };
}

interface PeriodData {
  month: string;
  revenue: number;
  anomaly?: 'spike' | 'drop';
  isLossPeriod?: boolean;
}

interface AnomalyResolverProps {
  anomalies: EnhancedAnomaly[];
  periodsData?: PeriodData[];
  totalTransactions?: number;
  totalMonths?: number;
  lossEventStart?: string;
  lossEventEnd?: string;
  onResolve: (anomalyId: string, reason: string, note: string) => void;
  onBatchResolve: (anomalyIds: string[], reason: string, note: string) => void;
  onUnresolve?: (anomalyId: string) => void;
  onViewTransaction?: (transactionId: string) => void;
  onForensicInsightFetched?: (anomalyId: string, insight: any) => void;
  loading?: boolean;
}

// Format currency
const fmt = (n: number) => "$" + Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 0 });

// Format currency compact
const fmtCompact = (n: number) => {
  const abs = Math.abs(n);
  if (abs >= 1000000) return "$" + (n / 1000000).toFixed(1) + "M";
  if (abs >= 1000) return "$" + (n / 1000).toFixed(1) + "K";
  return "$" + n.toFixed(0);
};

// Format percentage
const pct = (n: number) => (n > 0 ? "+" : "") + n.toFixed(1) + "%";

// Generate diagnostic AI suggestion based on context
function generateDiagnosticSuggestion(anomaly: EnhancedAnomaly, lossEventStart?: string): string {
  const isSpike = anomaly.deviation > 0;
  const absPct = Math.abs(anomaly.deviation);
  const delta = anomaly.to - anomaly.from;
  
  // Find the top category driving the change
  const topCategory = anomaly.categories?.reduce((max, cat) => 
    Math.abs(cat.delta) > Math.abs(max.delta) ? cat : max
  , anomaly.categories[0]);
  
  const topCatText = topCategory 
    ? `${fmtCompact(Math.abs(topCategory.delta))} ${isSpike ? 'increase' : 'drop'} in ${topCategory.name}`
    : '';

  // Check if this coincides with loss event
  if (lossEventStart && anomaly.period >= lossEventStart && !isSpike) {
    return `This period coincides with the claimed loss event start date. ${absPct.toFixed(0)}% decline driven by ${topCatText}. Recommend classifying as loss period onset.`;
  }
  
  if (anomaly.isLossPeriod && !isSpike) {
    return `Loss period impact confirmed. Revenue down ${fmtCompact(Math.abs(delta))} (${absPct.toFixed(0)}%) from baseline. ${topCatText}. Flag as covered loss for claim calculation.`;
  }
  
  if (anomaly.isPreLoss && isSpike) {
    return `Pre-loss spike detected. Revenue ${fmtCompact(delta)} above trend. ${topCatText}. Consider using this period in baseline calculation for stronger claim support.`;
  }
  
  if (anomaly.isPostLoss && isSpike) {
    return `Recovery period indicator. Revenue rebounding ${pct(anomaly.deviation)} toward baseline. ${topCatText}. May signal end of covered loss period.`;
  }
  
  // Generic but still diagnostic
  if (isSpike) {
    return `Unusual revenue spike of ${pct(anomaly.deviation)}. ${topCatText}. Review for one-time events, insurance proceeds, or timing differences that may need adjustment.`;
  } else {
    return `Revenue declined ${pct(Math.abs(anomaly.deviation))} from prior period. ${topCatText}. Verify if operational issue, seasonal pattern, or early loss indicator.`;
  }
}

// Enhanced Sparkline - shows clean revenue trend with minimal anomaly indicators
function Sparkline({ 
  data, 
  activeMonth,
  lossEventStart,
  lossEventEnd,
  onHoverMonth,
  onClickMonth
}: { 
  data: PeriodData[];
  activeMonth?: string;
  lossEventStart?: string;
  lossEventEnd?: string;
  onHoverMonth?: (month: string | null) => void;
  onClickMonth?: (month: string) => void;
}) {
  const [hovered, setHovered] = useState<number | null>(null);
  
  if (!data || data.length === 0) return null;
  
  const max = Math.max(...data.map(d => d.revenue));
  const min = Math.min(...data.map(d => d.revenue));
  const range = max - min || 1;
  const w = 100;
  const h = 40;
  const padding = 4;
  
  const points = data.map((d, i) => {
    const isInLossPeriod = lossEventStart && lossEventEnd && 
      d.month >= lossEventStart && d.month <= lossEventEnd;
    return {
      x: (i / Math.max(data.length - 1, 1)) * w,
      y: h - ((d.revenue - min) / range) * (h - padding * 2) - padding,
      isInLossPeriod,
      index: i,
      ...d
    };
  });
  
  // Build the path
  const pathD = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(" ");
  
  // Find loss period x range for shading
  const lossPoints = points.filter(p => p.isInLossPeriod);
  const lossStartX = lossPoints.length > 0 ? Math.min(...lossPoints.map(p => p.x)) : null;
  const lossEndX = lossPoints.length > 0 ? Math.max(...lossPoints.map(p => p.x)) : null;

  // Count anomalies for the indicator
  const anomalyCount = points.filter(p => p.anomaly).length;

  return (
    <div className="relative w-full h-full">
      {/* Tooltip - positioned outside overflow container */}
      {hovered !== null && points[hovered] && (
        <div 
          className="absolute -top-12 bg-gray-900 text-white text-xs px-3 py-2 rounded-lg shadow-lg whitespace-nowrap z-50 pointer-events-none"
          style={{ 
            left: `${Math.min(Math.max((points[hovered].x / w) * 100, 10), 90)}%`, 
            transform: 'translateX(-50%)' 
          }}
        >
          <div className="font-semibold">{points[hovered].month}</div>
          <div className="flex items-center gap-2 mt-0.5">
            <span>{fmtCompact(points[hovered].revenue)}</span>
            {points[hovered].anomaly && (
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                points[hovered].anomaly === 'drop' ? 'bg-red-500' : 'bg-amber-500'
              }`}>
                {points[hovered].anomaly === 'drop' ? 'DROP' : 'SPIKE'}
              </span>
            )}
          </div>
          {/* Arrow */}
          <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 bg-gray-900 rotate-45" />
        </div>
      )}
      
      {/* Chart container with overflow hidden */}
      <div className="w-full h-full overflow-hidden">
        <svg 
          viewBox={`0 0 ${w} ${h}`} 
          className="w-full h-full cursor-crosshair"
          preserveAspectRatio="xMidYMid meet"
          onMouseLeave={() => { setHovered(null); onHoverMonth?.(null); }}
        >
        <defs>
          <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#10b981" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="lossGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ef4444" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#ef4444" stopOpacity="0.05" />
          </linearGradient>
        </defs>
        
        {/* Loss period shading */}
        {lossStartX !== null && lossEndX !== null && (
          <rect 
            x={lossStartX} 
            y={0} 
            width={lossEndX - lossStartX + 2} 
            height={h}
            fill="url(#lossGrad)"
          />
        )}
        
        {/* Area fill under line */}
        <path d={pathD + `L${w},${h}L0,${h}Z`} fill="url(#sparkGrad)" />
        
        {/* Revenue line */}
        <path d={pathD} fill="none" stroke="#10b981" strokeWidth="1.5" />
        
        {/* Invisible hover zones for each point - no visible markers */}
        {points.map((p, i) => (
          <rect
            key={i}
            x={p.x - (w / points.length / 2)}
            y={0}
            width={w / points.length}
            height={h}
            fill="transparent"
            onMouseEnter={() => { setHovered(i); onHoverMonth?.(p.month); }}
            onClick={() => p.anomaly && onClickMonth?.(p.month)}
            className={p.anomaly ? 'cursor-pointer' : 'cursor-crosshair'}
          />
        ))}
        
        {/* Show a single marker only for hovered point */}
        {hovered !== null && points[hovered] && (
          <circle
            cx={points[hovered].x}
            cy={points[hovered].y}
            r={3}
            fill={points[hovered].anomaly === 'drop' ? '#ef4444' : points[hovered].anomaly === 'spike' ? '#f59e0b' : '#10b981'}
            stroke="white"
            strokeWidth="1.5"
          />
        )}
        
        {/* Active month marker (when card is expanded) */}
        {activeMonth && points.find(p => p.month === activeMonth) && (
          <circle
            cx={points.find(p => p.month === activeMonth)!.x}
            cy={points.find(p => p.month === activeMonth)!.y}
            r={4}
            fill={points.find(p => p.month === activeMonth)!.anomaly === 'drop' ? '#ef4444' : '#f59e0b'}
            stroke="white"
            strokeWidth="2"
          />
        )}
      </svg>
      </div>
      
      {/* Anomaly count indicator */}
      {anomalyCount > 0 && (
        <div className="absolute top-1 right-1 text-[10px] text-gray-400 bg-white/80 px-1 rounded">
          {anomalyCount} anomalies
        </div>
      )}
    </div>
  );
}

// Enhanced Category Breakdown with prev → curr format
function CategoryBreakdownDetail({ categories }: { categories: CategoryBreakdown[] }) {
  if (!categories || categories.length === 0) return null;
  
  // Sort by absolute delta
  const sorted = [...categories].sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
  const maxDelta = Math.max(...sorted.map(c => Math.abs(c.delta)));
  
  return (
    <div className="space-y-2.5">
      {sorted.slice(0, 5).map((cat, i) => {
        const isPositive = cat.delta > 0;
        const barWidth = maxDelta > 0 ? (Math.abs(cat.delta) / maxDelta) * 100 : 0;
        
        return (
          <div key={i} className="group">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="font-medium text-gray-700">{cat.name}</span>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-gray-500">{fmtCompact(cat.prev)}</span>
                <ArrowRight className="w-3 h-3 text-gray-400" />
                <span className="text-gray-700 font-medium">{fmtCompact(cat.curr)}</span>
                <span className={`font-bold ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
                  ({isPositive ? '+' : ''}{fmtCompact(cat.delta)})
                </span>
              </div>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all ${isPositive ? 'bg-emerald-500' : 'bg-red-400'}`}
                style={{ width: `${barWidth}%` }}
              />
            </div>
          </div>
        );
      })}
      {sorted.length > 5 && (
        <p className="text-xs text-gray-400 text-center pt-1">
          +{sorted.length - 5} more categories
        </p>
      )}
    </div>
  );
}

// Top Transactions List
function TopTransactions({ 
  transactions, 
  onViewTransaction 
}: { 
  transactions?: FlaggedTransaction[];
  onViewTransaction?: (id: string) => void;
}) {
  if (!transactions || transactions.length === 0) {
    return (
      <div className="text-center py-4 text-sm text-gray-400">
        <List className="w-5 h-5 mx-auto mb-1 opacity-50" />
        No transaction details available
      </div>
    );
  }
  
  // Sort by absolute amount to get top drivers
  const sorted = [...transactions].sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
  const top = sorted.slice(0, 4);
  
  return (
    <div className="space-y-1.5">
      {top.map((txn, i) => (
        <div 
          key={i}
          className={`flex items-center gap-2 p-2.5 rounded-lg text-sm ${
            txn.flag ? 'bg-amber-50 border border-amber-100' : 'bg-gray-50'
          }`}
        >
          {txn.flag && <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />}
          <span className="text-gray-500 text-xs w-20 flex-shrink-0">{txn.date}</span>
          <span className="flex-1 truncate text-gray-700">{txn.description}</span>
          {txn.category && (
            <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">
              {txn.category}
            </span>
          )}
          <span className={`font-medium flex-shrink-0 ${txn.amount > 0 ? 'text-emerald-600' : 'text-gray-700'}`}>
            {fmtCompact(txn.amount)}
          </span>
          {onViewTransaction && txn.id && (
            <button
              onClick={(e) => { e.stopPropagation(); onViewTransaction(txn.id!); }}
              className="p-1 hover:bg-gray-200 rounded opacity-50 hover:opacity-100 transition-opacity"
            >
              <ExternalLink className="w-3.5 h-3.5 text-gray-500" />
            </button>
          )}
        </div>
      ))}
      {transactions.length > 4 && (
        <p className="text-xs text-gray-400 text-center pt-1">
          +{transactions.length - 4} more transactions
        </p>
      )}
    </div>
  );
}

// Individual Anomaly Card
function AnomalyCard({
  anomaly,
  isExpanded,
  onToggle,
  onResolve,
  onViewTransaction,
  isResolved,
  lossEventStart,
  onForensicInsightFetched
}: {
  anomaly: EnhancedAnomaly;
  isExpanded: boolean;
  onToggle: () => void;
  onResolve: (reason: string, note: string) => void;
  onViewTransaction?: (txnId: string) => void;
  isResolved: boolean;
  lossEventStart?: string;
  onForensicInsightFetched?: (anomalyId: string, insight: any) => void;
}) {
  const [selectedReason, setSelectedReason] = useState('');
  const [note, setNote] = useState('');
  const [acceptedAI, setAcceptedAI] = useState(false);
  const [forensicInsight, setForensicInsight] = useState<any>(anomaly.forensic_analysis || null);
  const [loadingForensic, setLoadingForensic] = useState(false);

  const severityConfig = {
    critical: { bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-700' },
    high: { bg: 'bg-orange-50', border: 'border-orange-200', badge: 'bg-orange-100 text-orange-700' },
    medium: { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700' },
    low: { bg: 'bg-gray-50', border: 'border-gray-200', badge: 'bg-gray-100 text-gray-600' }
  };
  
  const config = severityConfig[anomaly.severity] || severityConfig.medium;
  const isSpike = anomaly.deviation > 0;
  
  // Check for cached forensic insight
  const hasCachedForensic = anomaly.forensic_insight || anomaly.forensic_analysis;
  
  // Generate diagnostic AI text
  const diagnosticAI = generateDiagnosticSuggestion(anomaly, lossEventStart);
  const aiText = forensicInsight?.forensic_insight || anomaly.forensic_insight || diagnosticAI;
  const aiConfidence = anomaly.aiConfidence || anomaly.ai_analysis?.confidence;

  // Auto-fetch forensic insight when card is expanded (if not cached)
  React.useEffect(() => {
    if (isExpanded && !hasCachedForensic && !forensicInsight && !loadingForensic) {
      handleGetForensicInsight();
    }
  }, [isExpanded]);

  // Get forensic accountant insight
  const handleGetForensicInsight = async () => {
    setLoadingForensic(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/anomalies/forensic-analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          anomaly: anomaly,
          periods: [],
          business_context: {}
        })
      });
      const data = await response.json();
      if (data.success) {
        setForensicInsight(data);
        // Notify parent to cache the insight
        if (onForensicInsightFetched) {
          onForensicInsightFetched(anomaly.id, data);
        }
      }
    } catch (error) {
      console.error('Failed to get forensic insight:', error);
    } finally {
      setLoadingForensic(false);
    }
  };

  const handleAcceptAI = () => {
    setAcceptedAI(true);
    // Auto-select reason based on context
    if (anomaly.isLossPeriod || (lossEventStart && anomaly.period >= lossEventStart && !isSpike)) {
      setSelectedReason('Loss event impact');
    } else if (anomaly.isPreLoss && isSpike) {
      setSelectedReason('Normal fluctuation');
    } else if (anomaly.isPostLoss && isSpike) {
      setSelectedReason('Recovery period');
    } else if (isSpike) {
      setSelectedReason('One-time event');
    } else {
      setSelectedReason('Seasonal variation');
    }
    setNote(aiText);
  };

  const handleResolve = () => {
    if (selectedReason) {
      onResolve(selectedReason, note);
    }
  };

  if (isResolved) {
    return (
      <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-green-600" />
          <span className="text-gray-600 text-sm">{anomaly.title}</span>
          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded ml-auto">
            {anomaly.resolution_reason || 'Resolved'}
          </span>
        </div>
        {anomaly.resolution_note && (
          <p className="text-xs text-gray-500 mt-1 ml-6 italic">
            "{anomaly.resolution_note.slice(0, 100)}..."
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={`rounded-xl border ${config.border} overflow-hidden transition-all duration-200`}>
      {/* Card Header */}
      <div 
        className={`${config.bg} p-4 cursor-pointer hover:brightness-95 transition-all`}
        onClick={onToggle}
      >
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isSpike ? 'bg-amber-100' : 'bg-red-100'}`}>
            {isSpike ? (
              <TrendingUp className="w-5 h-5 text-amber-600" />
            ) : (
              <TrendingDown className="w-5 h-5 text-red-600" />
            )}
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className={`px-2 py-0.5 text-xs font-semibold rounded ${config.badge}`}>
                {anomaly.severity.toUpperCase()}
              </span>
              {anomaly.isLossPeriod && (
                <span className="px-2 py-0.5 text-xs font-semibold rounded bg-purple-100 text-purple-700">
                  LOSS PERIOD
                </span>
              )}
              <span className="text-sm font-medium text-gray-900">{anomaly.type}</span>
              <span className={`text-sm font-bold ${isSpike ? 'text-amber-600' : 'text-red-600'}`}>
                {pct(anomaly.deviation)}
              </span>
            </div>
            
            <p className="text-sm text-gray-600">{anomaly.title || anomaly.description}</p>
            
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
              <span>Period: <strong className="text-gray-700">{anomaly.period}</strong></span>
              <span className="flex items-center gap-1">
                {fmtCompact(anomaly.from)} 
                <ArrowRight className="w-3 h-3" /> 
                {fmtCompact(anomaly.to)}
              </span>
            </div>
          </div>
          
          {/* Expand Button */}
          <button className="p-1 hover:bg-white/50 rounded-lg transition-colors">
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            )}
          </button>
        </div>
      </div>
      
      {/* Expanded Detail */}
      {isExpanded && (
        <div className="p-4 bg-white border-t border-gray-100">
          {/* Forensic Accountant Insight */}
          {forensicInsight && (
            <div className="rounded-lg p-4 mb-4 bg-indigo-50 border border-indigo-200">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-indigo-100">
                  <FileText className="w-4 h-4 text-indigo-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold tracking-wide text-indigo-700">
                      FORENSIC ACCOUNTANT ANALYSIS
                    </span>
                    {forensicInsight.claim_impact && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        forensicInsight.claim_impact === 'HELPS_CLAIM' 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : forensicInsight.claim_impact === 'HURTS_CLAIM'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-gray-100 text-gray-700'
                      }`}>
                        {forensicInsight.claim_impact.replace('_', ' ')}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-indigo-800 mb-2">
                    {forensicInsight.forensic_insight}
                  </p>
                  {forensicInsight.recommended_action && (
                    <p className="text-xs text-indigo-600 font-medium">
                      → {forensicInsight.recommended_action}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* AI Diagnostic Suggestion */}
          {aiText && !forensicInsight && (
            <div className={`rounded-lg p-4 mb-4 ${acceptedAI ? 'bg-emerald-50 border border-emerald-200' : 'bg-blue-50 border border-blue-200'}`}>
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${acceptedAI ? 'bg-emerald-100' : 'bg-blue-100'}`}>
                  {acceptedAI ? (
                    <Check className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <Sparkles className="w-4 h-4 text-blue-600" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-bold tracking-wide ${acceptedAI ? 'text-emerald-700' : 'text-blue-700'}`}>
                      AI DIAGNOSTIC
                    </span>
                    {aiConfidence && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${acceptedAI ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                        {aiConfidence}% confidence
                      </span>
                    )}
                  </div>
                  <p className={`text-sm ${acceptedAI ? 'text-emerald-800' : 'text-blue-800'}`}>
                    {aiText}
                  </p>
                  <div className="flex gap-2 mt-3">
                    {!acceptedAI && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleAcceptAI(); }}
                        className="px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                      >
                        <Check className="w-4 h-4" /> Accept & Use in Report
                      </button>
                    )}
                    {!forensicInsight && !loadingForensic && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleGetForensicInsight(); }}
                        className="px-4 py-1.5 bg-indigo-100 text-indigo-700 text-sm font-medium rounded-lg hover:bg-indigo-200 transition-colors flex items-center gap-2"
                      >
                        <FileText className="w-4 h-4" /> Deep Analysis
                      </button>
                    )}
                    {loadingForensic && (
                      <span className="px-4 py-1.5 text-sm text-indigo-600 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Analyzing...
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Two Column Layout: Categories + Transactions */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            {/* Category Breakdown */}
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                <TrendingDown className="w-3.5 h-3.5" />
                Category Breakdown
              </h4>
              <CategoryBreakdownDetail categories={anomaly.categories || []} />
            </div>
            
            {/* Top Transactions */}
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                <List className="w-3.5 h-3.5" />
                Top Transactions
              </h4>
              <TopTransactions 
                transactions={anomaly.transactions} 
                onViewTransaction={onViewTransaction}
              />
            </div>
          </div>
          
          {/* Source Document */}
          {anomaly.sourceDoc && (
            <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
              <FileText className="w-3 h-3" />
              <span>Source: {anomaly.sourceDoc}</span>
            </div>
          )}
          
          {/* Report Flow Notice */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4">
            <p className="text-xs text-slate-600 flex items-start gap-2">
              <FileText className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 mt-0.5" />
              <span>
                <strong>Expert Report Integration:</strong> Your resolution reason and note will automatically 
                appear in the final expert report narrative. Be specific — this becomes testimony support.
              </span>
            </p>
          </div>
          
          {/* Resolution Section */}
          <div className="pt-4 border-t border-gray-100">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Resolution
            </h4>
            
            {/* Reason Pills */}
            <div className="flex flex-wrap gap-2 mb-3">
              {RESOLUTION_REASONS.map(reason => (
                <button
                  key={reason}
                  onClick={(e) => { e.stopPropagation(); setSelectedReason(reason); }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    selectedReason === reason
                      ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-transparent'
                  }`}
                >
                  {reason}
                </button>
              ))}
            </div>
            
            {/* Note Input */}
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add analysis note (this appears in the expert report)..."
              className="w-full p-3 border border-gray-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 mb-3"
              rows={3}
              onClick={(e) => e.stopPropagation()}
            />
            
            {/* Resolve Button */}
            <button
              onClick={(e) => { e.stopPropagation(); handleResolve(); }}
              disabled={!selectedReason}
              className={`w-full py-3 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                selectedReason
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              <CheckCircle2 className="w-4 h-4" />
              {selectedReason ? `Resolve as "${selectedReason}"` : 'Select a reason to resolve'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Enhanced Batch Resolve Modal with Filters
function BatchResolveModal({
  visible,
  onClose,
  anomalies,
  resolvedIds,
  onBatchResolve,
  lossEventStart,
  lossEventEnd
}: {
  visible: boolean;
  onClose: () => void;
  anomalies: EnhancedAnomaly[];
  resolvedIds: string[];
  onBatchResolve: (ids: string[], reason: string, note: string) => void;
  lossEventStart?: string;
  lossEventEnd?: string;
}) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');

  const unresolvedAnomalies = anomalies.filter(a => !resolvedIds.includes(a.id));
  
  // Apply filter
  const filteredAnomalies = useMemo(() => {
    return unresolvedAnomalies.filter(a => {
      const isSpike = a.deviation > 0;
      const isDrop = a.deviation < 0;
      const isHigh = a.severity === 'high' || a.severity === 'critical';
      const isPreLoss = a.isPreLoss || (lossEventStart && a.period < lossEventStart);
      const isLossPeriod = a.isLossPeriod || (lossEventStart && lossEventEnd && 
        a.period >= lossEventStart && a.period <= lossEventEnd);
      
      switch (activeFilter) {
        case 'spikes': return isSpike;
        case 'drops': return isDrop;
        case 'high': return isHigh;
        case 'pre-loss': return isPreLoss;
        case 'loss-period': return isLossPeriod;
        default: return true;
      }
    });
  }, [unresolvedAnomalies, activeFilter, lossEventStart, lossEventEnd]);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectAllFiltered = () => {
    setSelectedIds(filteredAnomalies.map(a => a.id));
  };

  const clearSelection = () => {
    setSelectedIds([]);
  };

  const handleResolve = () => {
    if (selectedIds.length > 0 && reason) {
      onBatchResolve(selectedIds, reason, note);
      setSelectedIds([]);
      setReason('');
      setNote('');
      onClose();
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              Batch Resolve Anomalies
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Filter and resolve multiple anomalies with a shared explanation
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        
        {/* Filter Bar */}
        <div className="p-3 border-b border-gray-100 bg-gray-50 flex-shrink-0">
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            <Filter className="w-4 h-4 text-gray-400 flex-shrink-0" />
            {BATCH_FILTERS.map(filter => {
              const count = unresolvedAnomalies.filter(a => {
                const isSpike = a.deviation > 0;
                const isDrop = a.deviation < 0;
                const isHigh = a.severity === 'high' || a.severity === 'critical';
                const isPreLoss = a.isPreLoss || (lossEventStart && a.period < lossEventStart);
                const isLossPeriod = a.isLossPeriod || (lossEventStart && lossEventEnd && 
                  a.period >= lossEventStart && a.period <= lossEventEnd);
                
                switch (filter.id) {
                  case 'spikes': return isSpike;
                  case 'drops': return isDrop;
                  case 'high': return isHigh;
                  case 'pre-loss': return isPreLoss;
                  case 'loss-period': return isLossPeriod;
                  default: return true;
                }
              }).length;
              
              return (
                <button
                  key={filter.id}
                  onClick={() => { setActiveFilter(filter.id); setSelectedIds([]); }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all flex items-center gap-1.5 ${
                    activeFilter === filter.id
                      ? `bg-${filter.color}-100 text-${filter.color}-700 border border-${filter.color}-300`
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
                  }`}
                  style={activeFilter === filter.id ? {
                    backgroundColor: filter.color === 'gray' ? '#f3f4f6' : 
                      filter.color === 'amber' ? '#fef3c7' :
                      filter.color === 'red' ? '#fee2e2' :
                      filter.color === 'orange' ? '#ffedd5' :
                      filter.color === 'blue' ? '#dbeafe' :
                      filter.color === 'purple' ? '#f3e8ff' : '#f3f4f6',
                    borderColor: filter.color === 'gray' ? '#d1d5db' : 
                      filter.color === 'amber' ? '#fcd34d' :
                      filter.color === 'red' ? '#fca5a5' :
                      filter.color === 'orange' ? '#fdba74' :
                      filter.color === 'blue' ? '#93c5fd' :
                      filter.color === 'purple' ? '#c4b5fd' : '#d1d5db',
                  } : {}}
                >
                  {filter.label}
                  <span className="bg-white/50 px-1.5 py-0.5 rounded text-[10px]">{count}</span>
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Selection Actions */}
        <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <span className="text-sm text-gray-600">
            <strong>{selectedIds.length}</strong> of {filteredAnomalies.length} selected
          </span>
          <div className="flex gap-3">
            <button 
              onClick={clearSelection} 
              className="text-sm text-gray-500 hover:text-gray-700"
              disabled={selectedIds.length === 0}
            >
              Clear
            </button>
            <button 
              onClick={selectAllFiltered} 
              className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
            >
              Select All ({filteredAnomalies.length})
            </button>
          </div>
        </div>
        
        {/* Anomaly List */}
        <div className="flex-1 overflow-y-auto p-4">
          {filteredAnomalies.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Filter className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No anomalies match this filter</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredAnomalies.map(a => {
                const isSpike = a.deviation > 0;
                return (
                  <div
                    key={a.id}
                    onClick={() => toggleSelect(a.id)}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedIds.includes(a.id)
                        ? 'border-emerald-300 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        selectedIds.includes(a.id)
                          ? 'border-emerald-500 bg-emerald-500'
                          : 'border-gray-300'
                      }`}>
                        {selectedIds.includes(a.id) && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${
                        isSpike ? 'bg-amber-100' : 'bg-red-100'
                      }`}>
                        {isSpike ? (
                          <TrendingUp className="w-3.5 h-3.5 text-amber-600" />
                        ) : (
                          <TrendingDown className="w-3.5 h-3.5 text-red-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-700 truncate">{a.type}</span>
                          <span className={`text-sm font-bold ${isSpike ? 'text-amber-600' : 'text-red-600'}`}>
                            {pct(a.deviation)}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {fmtCompact(a.from)} → {fmtCompact(a.to)}
                        </div>
                      </div>
                      <span className="text-xs text-gray-500 flex-shrink-0">{a.period}</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded flex-shrink-0 ${
                        a.severity === 'high' || a.severity === 'critical'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {a.severity.toUpperCase()}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Resolution Form */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          {/* Reason Pills */}
          <div className="flex flex-wrap gap-2 mb-3">
            {RESOLUTION_REASONS.map(r => (
              <button
                key={r}
                onClick={() => setReason(r)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  reason === r
                    ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {r}
              </button>
            ))}
          </div>
          
          {/* Note */}
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Shared explanation for all selected anomalies (appears in report)..."
            className="w-full p-3 border border-gray-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-emerald-500 mb-3"
            rows={2}
          />
          
          {/* Action Button */}
          <button
            onClick={handleResolve}
            disabled={!selectedIds.length || !reason}
            className={`w-full py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
              selectedIds.length && reason
                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            <CheckCircle2 className="w-4 h-4" />
            Resolve {selectedIds.length} Anomalies as "{reason || '...'}"
          </button>
        </div>
      </div>
    </div>
  );
}

// Main Component
export default function AnomalyResolver({
  anomalies,
  periodsData,
  totalTransactions = 0,
  totalMonths = 0,
  lossEventStart,
  lossEventEnd,
  onResolve,
  onBatchResolve,
  onUnresolve,
  onViewTransaction,
  onForensicInsightFetched,
  loading = false
}: AnomalyResolverProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [resolvedIds, setResolvedIds] = useState<string[]>(() => 
    anomalies.filter(a => a.is_resolved).map(a => a.id)
  );
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [visibleCount, setVisibleCount] = useState(5);

  const unresolvedCount = anomalies.length - resolvedIds.length;
  const highPriorityCount = anomalies.filter(a => 
    !resolvedIds.includes(a.id) && (a.severity === 'high' || a.severity === 'critical')
  ).length;

  const handleResolve = (id: string, reason: string, note: string) => {
    setResolvedIds(prev => [...prev, id]);
    setExpandedId(null);
    onResolve(id, reason, note);
  };

  const handleBatchResolve = (ids: string[], reason: string, note: string) => {
    setResolvedIds(prev => [...new Set([...prev, ...ids])]);
    onBatchResolve(ids, reason, note);
  };

  // Generate periods data from anomalies if not provided
  // Build timeline data - prefer full periods data if available
  // If only anomalies, show a simplified bar chart instead of sparkline
  const timelineData = useMemo(() => {
    // If full periods data is provided, use it
    if (periodsData && periodsData.length > 0) {
      // Mark which periods have anomalies
      const anomalyPeriods = new Set(anomalies.map(a => a.period));
      return periodsData.map(p => ({
        ...p,
        anomaly: anomalyPeriods.has(p.month) 
          ? (anomalies.find(a => a.period === p.month)?.deviation || 0) > 0 ? 'spike' : 'drop'
          : undefined
      }));
    }
    
    // Fallback: build from anomalies - but aggregate by month to avoid duplicates
    // This won't show the full revenue trend, just anomaly periods
    const monthMap = new Map<string, { revenue: number; anomaly?: 'spike' | 'drop' }>();
    
    anomalies.forEach(a => {
      const anomalyType = a.deviation > 0 ? 'spike' : 'drop';
      // Only keep the most significant anomaly for each period
      const existing = monthMap.get(a.period);
      if (!existing || Math.abs(a.deviation) > Math.abs(existing.revenue)) {
        monthMap.set(a.period, { revenue: a.to, anomaly: anomalyType });
      }
    });
    
    return Array.from(monthMap.entries())
      .map(([month, data]) => ({ month, ...data }))
      .sort((a, b) => a.month.localeCompare(b.month));
  }, [anomalies, periodsData]);

  // Check if we have proper timeline data or just anomaly points
  const hasFullTimeline = periodsData && periodsData.length > 0;

  // Separate resolved and unresolved
  const unresolvedAnomalies = anomalies.filter(a => !resolvedIds.includes(a.id));
  const resolvedAnomalies = anomalies.filter(a => resolvedIds.includes(a.id));

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-center gap-4">
          <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
          <div>
            <p className="font-medium text-blue-900">Analyzing Financial Data...</p>
            <p className="text-sm text-blue-600">Detecting anomalies and generating AI insights</p>
          </div>
        </div>
      </div>
    );
  }

  if (anomalies.length === 0) {
    return (
      <div className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-xl p-6">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="w-6 h-6 text-emerald-600" />
          <div>
            <p className="font-medium text-emerald-900">No Anomalies Detected</p>
            <p className="text-sm text-emerald-600">AI verified data consistency across all periods</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
          <span className="font-semibold tracking-wide">FINANCIAL DATA</span>
          <span>•</span>
          <span>ANOMALY RESOLUTION</span>
        </div>
        <h2 className="text-2xl font-bold text-gray-900">
          {unresolvedCount} Anomal{unresolvedCount === 1 ? 'y' : 'ies'} to Review
        </h2>
        <p className="text-gray-600 mt-1">
          {totalTransactions.toLocaleString()} transactions across {totalMonths} months
          {highPriorityCount > 0 && (
            <span className="text-amber-600 ml-2">• {highPriorityCount} high priority</span>
          )}
          {resolvedIds.length > 0 && (
            <span className="text-emerald-600 ml-2">• {resolvedIds.length} resolved</span>
          )}
        </p>
      </div>

      {/* Anomaly Distribution Summary - shows when we have many anomalies */}
      {anomalies.length > 10 && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Anomaly Distribution
            </h3>
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                Spikes ({anomalies.filter(a => a.deviation > 0).length})
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-red-500" />
                Drops ({anomalies.filter(a => a.deviation < 0).length})
              </span>
            </div>
          </div>
          
          {/* Mini bar chart showing anomaly severity distribution */}
          <div className="grid grid-cols-4 gap-2 mb-3">
            {['critical', 'high', 'medium', 'low'].map(severity => {
              const count = anomalies.filter(a => a.severity === severity && !resolvedIds.includes(a.id)).length;
              const resolvedCount = anomalies.filter(a => a.severity === severity && resolvedIds.includes(a.id)).length;
              const total = count + resolvedCount;
              const colors = {
                critical: 'bg-red-500',
                high: 'bg-orange-500',
                medium: 'bg-amber-500',
                low: 'bg-gray-400'
              };
              return (
                <div key={severity} className="text-center">
                  <div className="h-12 bg-gray-100 rounded relative overflow-hidden flex flex-col justify-end">
                    {resolvedCount > 0 && (
                      <div 
                        className={`${colors[severity as keyof typeof colors]} opacity-30`}
                        style={{ height: `${(resolvedCount / Math.max(...anomalies.map(() => 1).reduce((acc, _, i) => {
                          const s = ['critical', 'high', 'medium', 'low'][Math.floor(i / (anomalies.length / 4))];
                          acc[s] = (acc[s] || 0) + 1;
                          return acc;
                        }, {} as Record<string, number>), Object.values(anomalies.reduce((acc, a) => {
                          acc[a.severity] = (acc[a.severity] || 0) + 1;
                          return acc;
                        }, {} as Record<string, number>)))) * 100}%` }}
                      />
                    )}
                    <div 
                      className={colors[severity as keyof typeof colors]}
                      style={{ height: `${(count / Math.max(...Object.values(anomalies.reduce((acc, a) => {
                        acc[a.severity] = (acc[a.severity] || 0) + 1;
                        return acc;
                      }, {} as Record<string, number>)))) * 100}%` }}
                    />
                  </div>
                  <div className="mt-1 text-xs font-medium text-gray-600 capitalize">{severity}</div>
                  <div className="text-[10px] text-gray-400">{count} left</div>
                </div>
              );
            })}
          </div>
          
          {/* Period range */}
          {timelineData.length >= 2 && (
            <div className="flex justify-between text-xs text-gray-400 border-t border-gray-100 pt-3">
              <span>First: {timelineData[0].month}</span>
              <span>Latest: {timelineData[timelineData.length - 1].month}</span>
            </div>
          )}
        </div>
      )}

      {/* Revenue Timeline - only show if we have proper period data (not just anomalies) */}
      {hasFullTimeline && timelineData.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Revenue Timeline
            </h3>
            <div className="flex items-center gap-4 text-xs text-gray-500">
              {lossEventStart && (
                <span className="flex items-center gap-1">
                  <span className="w-3 h-2 rounded bg-red-200" />
                  Loss Period
                </span>
              )}
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                Spike
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-red-500" />
                Drop
              </span>
            </div>
          </div>
          <div className="h-24 relative">
            <Sparkline 
              data={timelineData} 
              activeMonth={expandedId ? anomalies.find(a => a.id === expandedId)?.period : undefined}
              lossEventStart={lossEventStart}
              lossEventEnd={lossEventEnd}
              onClickMonth={(month) => {
                const anomaly = anomalies.find(a => a.period === month);
                if (anomaly) setExpandedId(anomaly.id);
              }}
            />
          </div>
          {timelineData.length >= 2 && (
            <div className="flex justify-between mt-2 text-xs text-gray-400">
              <span>{timelineData[0].month}</span>
              <span>{timelineData[timelineData.length - 1].month}</span>
            </div>
          )}
        </div>
      )}

      {/* Action Bar */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => setShowBatchModal(true)}
          className="px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 font-medium text-sm hover:bg-amber-100 transition-colors flex items-center gap-2"
        >
          <Zap className="w-4 h-4" />
          Batch Resolve ({unresolvedCount})
        </button>
        <div className="flex-1" />
        <span className="text-sm text-gray-500">
          {resolvedIds.length}/{anomalies.length} resolved
        </span>
        <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all duration-500"
            style={{ width: `${(resolvedIds.length / anomalies.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Unresolved Anomaly Cards */}
      <div className="space-y-3">
        {unresolvedAnomalies.slice(0, visibleCount).map(anomaly => (
          <AnomalyCard
            key={anomaly.id}
            anomaly={anomaly}
            isExpanded={expandedId === anomaly.id}
            onToggle={() => setExpandedId(expandedId === anomaly.id ? null : anomaly.id)}
            onResolve={(reason, note) => handleResolve(anomaly.id, reason, note)}
            onViewTransaction={onViewTransaction}
            isResolved={false}
            lossEventStart={lossEventStart}
            onForensicInsightFetched={onForensicInsightFetched}
          />
        ))}
        
        {/* Show More Button */}
        {unresolvedAnomalies.length > visibleCount && (
          <button
            onClick={() => setVisibleCount(prev => prev + 10)}
            className="w-full py-3 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors text-sm font-medium"
          >
            Show {Math.min(10, unresolvedAnomalies.length - visibleCount)} More 
            ({unresolvedAnomalies.length - visibleCount} remaining)
          </button>
        )}
      </div>

      {/* Resolved Section */}
      {resolvedAnomalies.length > 0 && (
        <div className="pt-4 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Resolved ({resolvedAnomalies.length})
          </h3>
          <div className="space-y-2">
            {resolvedAnomalies.slice(0, 3).map(anomaly => (
              <AnomalyCard
                key={anomaly.id}
                anomaly={anomaly}
                isExpanded={false}
                onToggle={() => {}}
                onResolve={() => {}}
                isResolved={true}
              />
            ))}
            {resolvedAnomalies.length > 3 && (
              <p className="text-xs text-gray-400 text-center py-2">
                +{resolvedAnomalies.length - 3} more resolved
              </p>
            )}
          </div>
        </div>
      )}

      {/* Batch Resolve Modal */}
      <BatchResolveModal
        visible={showBatchModal}
        onClose={() => setShowBatchModal(false)}
        anomalies={anomalies}
        resolvedIds={resolvedIds}
        onBatchResolve={handleBatchResolve}
        lossEventStart={lossEventStart}
        lossEventEnd={lossEventEnd}
      />
    </div>
  );
}
